/**
 * src/api/index.ts — A COSTURA. Um lugar só decide de onde vem a programação.
 *
 * Este é o arquivo que o dev-pleno NÃO precisa reescrever para trocar o stub pelo
 * HTTP de verdade: `createGateway()` devolve o `StubGateway` em demonstração e o
 * gateway registrado no caso normal. `src/player/**` e `src/ui/**` só conhecem a
 * interface `ReceptorGateway` — não sabem qual das duas está rodando.
 *
 * COMO A DEMO É LIGADA (duas portas, de propósito):
 *  - `?demo=1` ou `#demo` na URL — é o que o Lucas abre no browser para ver o loop;
 *  - `VITE_DEMO=1` no build — para uma build de TV já em demonstração, sem depender
 *    de alguém lembrar de digitar a query.
 *
 * Sem nenhum dos dois, o receptor usa o gateway REGISTRADO — e, enquanto ninguém
 * registrou um, cai no stub avisando no log (nunca na TV).
 *
 * O registro permite injetar gateways nos testes. O boot registra HttpGateway;
 * net/** participa do build/typecheck. Não há bloqueio de sintaxe na rede.
 */
import { StubGateway } from './StubGateway';
import type { ReceptorGateway } from './types';

export type { HeartbeatResult, QueuePayload, ReceptorGateway } from './types';
export { StubGateway, demoAds, stubImageBlob } from './StubGateway';

/** Construtor que o dev-pleno registra. Mesma forma de opções do `HttpGateway` dele. */
export type ReceptorGatewayFactory = (options: {
  deviceToken: string;
  screenId: string;
  platform?: 'web' | 'android' | 'androidtv';
  appVersion?: string;
}) => ReceptorGateway;

let httpGatewayFactory: ReceptorGatewayFactory | null = null;

/**
 * REGISTRA o gateway de produção. É a costura de uma linha:
 *
 *   import { registerHttpGateway } from './api';
 *   import { HttpGateway } from '../net/HttpGateway';
 *   registerHttpGateway(opts => new HttpGateway(opts));
 *
 * Registro explícito no boot; testes podem substituir/resetar a fábrica.
 */
export function registerHttpGateway(factory: ReceptorGatewayFactory | null): void {
  httpGatewayFactory = factory;
}

/** `true` quando existe gateway HTTP registrado (o `main.ts` usa para avisar). */
export function hasHttpGateway(): boolean {
  return httpGatewayFactory !== null;
}

/** O que `createGateway` devolveu — a casca usa isto para a tela de espera e o log. */
export interface GatewayBundle {
  gateway: ReceptorGateway;
  /** `'stub'` = demonstração/memória (funciona sem backend). `'http'` = rede real. */
  mode: 'stub' | 'http';
  /** Mensagem para o LOG quando cai no stub. NUNCA vai para a TV (§5.2). */
  warning?: string;
  /** Rótulo da tela para a tela de espera; no HTTP, a casca usa o `name` do heartbeat. */
  screenLabel?: string;
}

/** `?demo=1` / `#demo` / `?demo` (flag sem valor) — e `0`/`false` desativam. */
export function isDemoRequested(search: string, hash: string): boolean {
  const params = new URLSearchParams(search);
  const demo = params.get('demo');
  if (demo !== null && demo !== '0' && demo.toLowerCase() !== 'false') {
    return true;
  }
  return hash.replace(/^#/, '') === 'demo';
}

/** `VITE_DEMO=1` no build. `import.meta.env` só existe sob o Vite — daí o guard. */
export function isDemoBuilt(): boolean {
  const env = (import.meta as { env?: Record<string, string | undefined> }).env;
  const value = env?.['VITE_DEMO'];
  return value === '1' || value === 'true';
}

export interface CreateGatewayOptions {
  /** Sobrescreve a detecção de demo (usado nos testes da casca). */
  demo?: boolean;
  /** Credenciais do storage do navegador (produção). */
  deviceToken?: string;
  screenId?: string;
  /** Relógio injetável — motor e gateway compartilham o MESMO `now`. */
  now?: () => Date;
  /** Latência do stub (ms). 0 nos testes; ~120 na demo para a UI não piscar. */
  latencyMs?: number;
}

/**
 * Cria o gateway da execução. NUNCA lança — uma tela preta numa TV é o pior modo de
 * falha do produto, então qualquer problema na rede vira stub + aviso no log.
 */
export function createGateway(options: CreateGatewayOptions = {}): GatewayBundle {
  const demo =
    options.demo ??
    (typeof window === 'undefined'
      ? isDemoBuilt()
      : isDemoRequested(window.location.search, window.location.hash) || isDemoBuilt());

  if (demo) {
    return { gateway: newStub(options), mode: 'stub', screenLabel: 'TV Demonstração' };
  }

  if (httpGatewayFactory && options.deviceToken && options.screenId) {
    return {
      gateway: httpGatewayFactory({
        deviceToken: options.deviceToken,
        screenId: options.screenId,
        platform: 'web',
      }),
      mode: 'http',
    };
  }

  // Sem gateway HTTP registrado, ou sem credencial: ABRE MESMO ASSIM, em memória.
  return {
    gateway: newStub(options),
    mode: 'stub',
    warning: !httpGatewayFactory
      ? 'HttpGateway não registrado (net/ do dev-pleno ainda em obras): abrindo em memória.'
      : 'Sem deviceToken/screenId no storage: abrindo em memória.',
  };
}

function newStub(options: CreateGatewayOptions): StubGateway {
  return new StubGateway({
    ...(options.now ? { now: options.now } : {}),
    latencyMs: options.latencyMs ?? 0,
    // Na demo o SSE dispara sozinho: sem isso ninguém veria a fila mudar sem
    // recarregar a página, e o critério 2 (< 60 s) não teria como ser inspecionado.
    realtimeOnSubscribe: true,
  });
}
